import {GitHubAvatar} from '@github-ui/github-avatar'
import {useAnalytics} from '@github-ui/use-analytics'
import {CheckIcon, ChevronRightIcon} from '@primer/octicons-react'
import {RelativeTime} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useState} from 'react'

import type {useRecentAgentActivity_issue$key} from './__generated__/useRecentAgentActivity_issue.graphql'
import styles from './RecentAgentActivity.module.css'
import type {AgentAction} from './use-recent-agent-activity'
import {useRecentAgentActivity} from './use-recent-agent-activity'

type ActionRowProps = {
  action: AgentAction
}

const ActionRow = memo(function ActionRow({action}: ActionRowProps) {
  return (
    <div className={styles.actionRow} role="listitem">
      <CheckIcon size={16} className={styles.checkIcon} aria-hidden="true" />
      <span className={styles.fieldName}>{action.fieldName}</span>
      <span className={styles.arrow} aria-hidden="true">
        →
      </span>
      <span className={styles.fieldValue}>{action.value ?? ''}</span>
      <span className={styles.timestamp}>
        <RelativeTime date={new Date(action.createdAt)} />
      </span>
    </div>
  )
})

type RecentAgentActivityProps = {
  issue: useRecentAgentActivity_issue$key
}

export function RecentAgentActivity({issue}: RecentAgentActivityProps) {
  const {hasRecentActivity, groups, totalActionCount, viewerCanUpdateMetadata} = useRecentAgentActivity(issue)
  const [expanded, setExpanded] = useState(false)
  const {sendAnalyticsEvent} = useAnalytics()

  const handleToggle = useCallback(() => {
    const next = !expanded
    setExpanded(next)
    sendAnalyticsEvent(
      next ? 'issues.recent_agent_activity.expanded' : 'issues.recent_agent_activity.collapsed',
      'RECENT_AGENT_ACTIVITY',
      {
        action_count: totalActionCount,
        agent_count: groups.length,
      },
    )
  }, [expanded, sendAnalyticsEvent, totalActionCount, groups.length])

  if (!hasRecentActivity || !viewerCanUpdateMetadata) {
    return null
  }

  const primaryGroup = groups[0]
  if (!primaryGroup) return null

  return (
    <div className={styles.container} data-testid="recent-agent-activity">
      <button type="button" className={styles.trigger} onClick={handleToggle} aria-expanded={expanded}>
        <span className={styles.label}>Recent agent activity</span>
        <span className={styles.dot} aria-hidden="true">
          ·
        </span>
        <span className={styles.actorInfo}>
          <GitHubAvatar src={primaryGroup.actorAvatarUrl} size={20} alt="" />
          <span className={styles.actorName}>{primaryGroup.actorLogin}</span>
          <span className={styles.badge}>via {primaryGroup.agentName}</span>
          <span className={styles.count}>{totalActionCount} applied</span>
        </span>
        <ChevronRightIcon
          size={16}
          className={clsx(styles.chevron, expanded && styles.chevronExpanded)}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div className={styles.content} role="list" aria-label="Agent actions">
          {groups.map(group =>
            group.actions.map(action => (
              <ActionRow
                key={`${group.agentSlug}-${action.fieldName}-${action.value}-${action.createdAt}`}
                action={action}
              />
            )),
          )}
        </div>
      )}
    </div>
  )
}
